import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from "react-router-dom";
import { Grid, Button } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import useStyles from './LabResultDetails.styles';
import ConfirmDialog from '../../components/ConfirmDialog';
import PreviewDialog from '../../components/PreviewDialog';
import { Form, Field, FormSpy } from 'react-final-form';
import { TextField, Select, Radios, makeRequired } from 'mui-rff';
import LabResultTable from '../../components/LabResultTable';
import arrayMutators from 'final-form-arrays';
import { makeValidate } from 'mui-rff';
import * as Yup from 'yup';
import { selectCurrentLabResult, loadLabResultById, selectCurrentError, updateLabResultById, selectLoading } from '../../state';
import { RecursivePartial } from '../../../../interfaces/recursive-partial';
import orderBy from 'lodash.orderby';
import FlagIcon from '@material-ui/icons/Flag';
import { format } from 'date-fns';
import { Mutator } from 'final-form';
import { PhysicianNormality, PhysicianOversight } from '../../enums/lab-result.enum';
import { LabResult, LabResultAnalyte } from '../../../../interfaces/lab-result';

interface FormSchema {
  reviewingPhysician: string;
  computedNormality: string;
  physicianNormality: PhysicianNormality;
  physicianOversight: PhysicianOversight;
  introComment: string;
  closingComment: string;
  contact: string;
  components: ComponentFormSchema[];
};

interface ComponentFormSchema {
  id: string;
  metadata: {
    physicianNormality: PhysicianNormality;
    physicianComment: string;
    visible: boolean;
  }
};

const schema = Yup.object().shape({
  reviewingPhysician: Yup.string(),
});

const validate = makeValidate(schema);

const required = makeRequired(schema);

function formatDob(date: string) {
  if (date.includes('-')) {
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  }
  return date;
}

function formatDate(date: string) {
  if (date) {
    try {
      return format(new Date(date), 'dd/MM/yyyy, hh:mm aaa');
    } catch(e) {
      return date;
    }
  }
  return date;
}

const setValue: Mutator<FormSchema, Partial<any>> = ([name, newValue], state, { changeValue }) => {
  changeValue(state, name, () => newValue);
};

export default function LabeResultDetails() {
  const error = useSelector(selectCurrentError);
  const classes = useStyles();
  const { id } = useParams<{id: string}>();
  const labResult = useSelector(selectCurrentLabResult);
  const loading = useSelector(selectLoading);
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);

  useEffect(() => {
    stableDispatch(loadLabResultById(id));
  }, [stableDispatch, id]);

  if (error) {
    // TODO: implement correctly with retry operation
    return (
      <div className={classes.error}>
        {error}
      </div>
    )
  }

  if (loading || !labResult) {
    return (
      <div>Loading...</div>
    );
  }

  function onSubmit(values: FormSchema) {
    const {
      reviewingPhysician = '',
      physicianNormality = PhysicianNormality.EMPTY,
      physicianOversight,
      introComment = '',
      closingComment = '',
      contact = '',
      components,
    } = values;
    const toUpdate: RecursivePartial<LabResult> = {
      metadata: {
        physicianOversight,
        reviewingPhysician,
        physicianNormality,
        introComment,
        closingComment,
        contact
      },
      results: components.map((comp) => ({
        id: comp.id,
        metadata: {
          physicianNormality: comp.metadata.physicianNormality || PhysicianNormality.EMPTY,
          physicianComment: comp.metadata.physicianComment || '',
          visible: comp.metadata.visible
        }
      }))
    };
    dispatch(updateLabResultById(id, toUpdate));
  }

  const initialValues = {
    reviewingPhysician: labResult?.metadata.reviewingPhysician || '',
    computedNormality: labResult?.metadata.globalNormality || '',
    physicianNormality: labResult?.metadata.physicianNormality || '',
    physicianOversight: labResult?.metadata.physicianOversight,
    introComment: labResult.metadata.introComment || `Please remember: all following comments have been made without any knowledge of your past medical history, and on the assumption that you are fit and healthy. Below are comments associated with your results which you may find useful. If you wish to discuss these further, please contact your GP.`,
    closingComment: labResult.metadata.closingComment || '',
    contact: labResult?.metadata.contact || '',
    components: orderBy(labResult.results, ['metadata.category.categoryOrderNumber', 'metadata.category.orderNumberInCategory']),
  };

  return (
    <React.Fragment>
      <Form 
        onSubmit={onSubmit}
        initialValues={initialValues}
        subscription={{}}
        mutators={{
          ...arrayMutators,
          setValue
        }}
        validate={validate as any}
        render={() => (
          <React.Fragment>
            <form noValidate>
              <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                className={classes.root}>
                <Grid container
                  direction="row"
                  justify="flex-end"
                  alignItems="flex-start">
                  <Card className={classes.metadata}>
                    <CardHeader
                      title={`Lab request: ${labResult.labRequestId}`}
                      subheader={`Lab result: ${labResult.id}`}
                    />
                    <CardContent>
                      <Typography variant="body1" component="p">
                        Status: {labResult.metadata.physicianOversight}
                      </Typography>
                      <Typography variant="body1" component="p">
                        DOB: {formatDob(labResult.user.dob)}
                      </Typography>
                      <Typography variant="body1" component="p">
                        Gender: {labResult.user.gender}
                      </Typography>
                      <Typography variant="body1" component="p">
                        Sample date: {formatDate(labResult.sampleTakenDate)}
                      </Typography>
                      <Typography variant="body1" component="p">
                        Sample received: {formatDate(labResult.metadata.requestedDate)}
                      </Typography>
                      <Typography variant="body1" component="p">
                        Result reported: {formatDate(labResult.metadata.observationDate)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <div className={classes.reviewingPhysicianContainer}>
                    <TextField
                      required={required.reviewingPhysician}
                      className={classes.reviewingPhysician}
                      label="Reviewing Physician"
                      id="physician-name"
                      name="reviewingPhysician"
                    />
                  </div>
                </Grid>
                <Grid container
                  direction="row"
                  justify="flex-end"
                  alignItems="center"
                  className={classes.normality}>
                  <div className={classes.formControl}>
                    <InputLabel id="computed-normality-label">Computed Normality</InputLabel>
                    <Field name="computedNormality">
                      {(props) => (
                        <Select
                          disabled
                          name={props.input.name}
                          className={props.input.value.computedNormality !== "Normal" ? classes.red : ""}
                          labelId="computed-normality-label"
                          id="computed-normality"
                        >
                          <MenuItem value={""}><br /></MenuItem>
                          <MenuItem value={"Normal"}>Normal</MenuItem>
                          <MenuItem className={classes.abnormal} value={"Abnormal"}>Abnormal</MenuItem>
                          <MenuItem className={classes.critical} value={"Very Abnormal"}>Very Abnormal</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </div>
                  <div className={classes.formControl}>
                    <InputLabel id="physician-normality-label">Physician Normality</InputLabel>
                    <Field name="physicianNormality">
                      {(props) => (
                        <Select
                          name={props.input.name}
                          className={props.input.value !==  "Normal" ? classes.red : ""}
                          labelId="physician-normality-label"
                          id="physician-normality"
                        >
                          <MenuItem value={""}><br /></MenuItem>
                          <MenuItem value={"Normal"}>Normal</MenuItem>
                          <MenuItem className={classes.abnormal} value={"Abnormal"}>Abnormal</MenuItem>
                          <MenuItem className={classes.critical} value={"Critical"}>Critical</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </div>
                </Grid>
                <TextField
                  className={classes.initialComments}
                  id="initial-comments"
                  label="Introductory comments"
                  multiline
                  rows={4}
                  rowsMax={Infinity}
                  name="introComment"
                />
              </Grid>
              <LabResultTable />
              <Grid
                container
                direction="row"
                justify="space-evenly"
                alignItems="flex-start">
                <TextField
                  className={classes.closingComment}
                  name="closingComment"
                  id="closing-comment"
                  label="Closing comment"
                  multiline
                  rows={4}
                  rowsMax={Infinity}/>
                <ClosingContainer labResult={labResult} />
              </Grid>
            </form>
          </React.Fragment>
        )}
      />
    </React.Fragment>
  );

}

function ClosingContainer({labResult}: {labResult: LabResult}) {
  const classes = useStyles();

  const isCompleted = labResult.metadata.physicianOversight === PhysicianOversight.COMPLETED_PHYSICIAN;
  const isFinalReview = labResult.metadata.physicianOversight === PhysicianOversight.FINAL_REVIEW;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  const openPreview = () => setPreviewOpen(true);
  const closePreview = () => setPreviewOpen(false);

  return (
    <div className={classes.closingContainer}>
      <div className={classes.contactContainer}>
        <Field name="contact">
          {(props) => (
            <React.Fragment>
              <Radios
                radioGroupProps={{row: true}}
                label="Contact"
                name={props.input.name}
                data={[
                  {label: 'Email', value: 'email'},
                  {label: 'Email and phone', value: 'email+phone'},
                  {label: 'Neither', value: 'neither'}
                ]}
              />
              <FormSpy>
                {(props) => {
                  const {contact} = props.values;
                  const {email, contactTelephone, firstName} = labResult.user;
                  const {prefix, number} = (contactTelephone || {});
                  const hasPhone = !!number;
                  const message = contact === 'email+phone' ? (
                    <React.Fragment>
                      {
                        !hasPhone ? <React.Fragment>
                          <b>There is no phone number available for this user</b>
                          <br/>
                        </React.Fragment> : null
                      }
                      <p>First name: {firstName}</p>
                      <p>Email: {email}</p>
                      <p>Phone: {!hasPhone ? 'No contact number available' : prefix+number}</p>
                    </React.Fragment>
                  ) : contact === 'email' ? (
                    <React.Fragment>
                      <p>First name: {firstName}</p>
                      <p>Email: {email}</p>
                    </React.Fragment>
                  ) : '';

                  return (
                    <ConfirmDialog
                      open={confirmOpen}
                      onClose={closeConfirm}
                      message={message}
                      onConfirm={() => {
                        props.form.mutators.setValue('physicianOversight', PhysicianOversight.COMPLETED_PHYSICIAN);
                        props.form.submit();
                      }}
                    />
                  )
                }}
              </FormSpy>
            </React.Fragment>
          )}
        </Field>
      </div>
      <FormSpy>
        {(props) => {
          return (
            <div className={classes.buttonsContainer}>
              { labResult.metadata.rateOfAgingEligible === true ? <div>
                  <FlagIcon fontSize="small" /> This lab result is RoA eligible
                </div> : ''}
              <Button variant="contained" disabled={isCompleted} onClick={openPreview}>Preview</Button>
              <Button variant="contained" disabled={isCompleted} onClick={props.form.submit}>Save</Button>
              <Button variant="contained" disabled={isCompleted || isFinalReview} onClick={() => {
                props.form.mutators.setValue('physicianOversight', PhysicianOversight.FINAL_REVIEW);
                props.form.submit();
              }}>Final Review</Button>
              <Button variant="contained" disabled={!props.valid || !props.values.reviewingPhysician || isCompleted}
                color="secondary" onClick={openConfirm}>
                Complete
              </Button>
              { isCompleted ? <div className={classes.red}>
                  This lab result oversight is completed
                </div> : ''}
            </div>
          )
        }}
      </FormSpy>
      <FormSpy>
        {(props) => {
          const {introComment, closingComment, components, reviewingPhysician} = props.values;
          const componentsComments = components
            .map((comp: LabResultAnalyte) => comp.metadata.physicianComment)
            .filter((comment: string) => !!comment)
            .join('\n\n');
          const previewText = `${introComment}\n\n${componentsComments}\n\n${closingComment}\n\n${reviewingPhysician}`
          return (
            <PreviewDialog
              open={previewOpen}
              onClose={closePreview}
              text={previewText}
            />
          )
        }}
      </FormSpy>
    </div>
  )
}